import { i18nReporting } from 'models/i18n';
import {
  AggregationFn,
  BenefitCostType,
  BenefitEvaluation,
  EconomicBenefitReportFields
} from 'views/modules/Reporting/EconomicBenefitReports/economicBenefitUtils';

const reporting: i18nReporting = {
  economicBenefit: {
    aggregation: {
      [AggregationFn.MEAN]: 'Daily average',
      [AggregationFn.CUMULATIVE_SUM]: 'Cumulative sum'
    },
    aggregationLabel: 'Aggregation',
    aggregationFrom: 'Date from',
    aggregationTo: 'Date to',
    evaluationDate: 'Date',
    evaluation: {
      [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY]: 'Daily',
      [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH]: 'Monthly'
    },
    exportToCsv: {
      dialogTitle: 'Export report to CSV',
      evaluationSelectLabel: 'Report granularity',
      openExportModalButton: 'Export CSV',
      reportType: {
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_PERIOD]: 'Periodic',
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_HOUR]: 'Hourly',
        [BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY]: 'Daily'
      },
      columns: {
        [EconomicBenefitReportFields.MONTH]: 'Month',
        [EconomicBenefitReportFields.MARKET_DAY]: 'Market day',
        [EconomicBenefitReportFields.MARKET_PERIOD_START]: 'Period start',
        [EconomicBenefitReportFields.MARKET_PERIOD_END]: 'Period end',
        [EconomicBenefitReportFields.MARKET_HOUR_START]: 'Hour start',
        [EconomicBenefitReportFields.MARKET_HOUR_END]: 'Hour end',
        [EconomicBenefitReportFields.BALANCING_GROUP_ID]: 'Balancing group',
        [EconomicBenefitReportFields.POSITIVE_IMBALANCE_MWH]:
          'Positive imbalance [MWh]',
        [EconomicBenefitReportFields.NEGATIVE_IMBALANCE_MWH]:
          'Negative imbalance [MWh]',
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_PRICE_EUR]:
          'Total imbalance price [Eur]',
        [EconomicBenefitReportFields.IMBALANCE_UNIT_PRICE_EUR]:
          'Imbalance unit price [Eur/MWh]',
        [EconomicBenefitReportFields.POSITIVE_IMBALANCE_MODEL_MWH]:
          'Positive imbalance model [MWh]',
        [EconomicBenefitReportFields.NEGATIVE_IMBALANCE_MODEL_MWH]:
          'Negative imbalance model [MWh]',
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_MODEL_PRICE_EUR]:
          'Total imbalance model price [Eur]',
        [EconomicBenefitReportFields.IMBALANCE_MODEL_UNIT_PRICE_EUR]:
          'Imbalance model unit price [Eur/MWh]',
        [EconomicBenefitReportFields.IMBALANCE_PRICE_DIFFERENCE_EUR]:
          'Imbalance price difference [Eur]',
        [EconomicBenefitReportFields.IMBALANCE_UNIT_PRICE_DIFFERENCE_EUR]:
          'Imbalance unit price difference [Eur/MWh]',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_PRICE_EUR]:
          'Reserved capacity exceeding price [Eur]',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_COUNT]:
          'Reserved capacity exceeding count',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_DETECTED_COUNT]:
          'Reserved capacity exceeding detected count',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_MWH]:
          'Reserved capacity exceeding [MWh]',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_DETECTED_MWH]:
          'Reserved capacity exceeding detected [MWh]',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_PRICE_DIFFERENCE_EUR]:
          'Reserved capacity exceeding price difference [Eur]',
        [EconomicBenefitReportFields.RESERVED_CAPACITY_EXCEEDING_DETECTED_PRICE_EUR]:
          'Reserved capacity exceeding detected price [Eur]'
      }
    },
    tableColumns: {
      realBenefit: 'Real benefit',
      difference: 'Difference',
      withAORTA: 'With AORTA',
      withoutAORTA: 'Without AORTA'
    },
    tableRows: {
      deviation: 'Deviation',
      unitCostOfDeviation: 'Unit cost of deviation',
      deviationSize: 'Deviation size',
      costOfExceedingReservedCapacity: 'Cost of exceeding RC'
    },
    tableTooltip: {
      energy: 'Energy',
      exceedingCount: 'Exceeding count'
    },
    charts: {
      benefitChartsTitle: 'Benefits on deviation',
      cumulativeBenefitsChartTitle: 'Cumulative benefits on deviation',
      cost: {
        [BenefitCostType.SUMMARY]: 'Summary',
        [BenefitCostType.UNIT]: 'Unit'
      },
      labels: { period: 'Period', day: 'Day' },
      series: {
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_PRICE_EUR]: 'With AORTA',
        [EconomicBenefitReportFields.TOTAL_IMBALANCE_MODEL_PRICE_EUR]:
          'Without AORTA'
      }
    }
  },
  tableColumns: {
    date: 'Date',
    period: 'Period',
    period_start: 'Period start',
    period_end: 'Period end',
    system_imbalance_mwh: 'System imbalance [MWh]',
    system_imbalance_eur_mwh: 'System imbalance price [Eur/MWh]',
    predicted_value_d1: 'Predicted value D+1 [MWh]',
    predicted_value_hx: 'Predicted value H+X [MWh]',
    prediction_success_d1: 'Prediction success D+1 [MWh]',
    prediction_success_hx: 'Prediction success H+X [MWh]',
    ems_party_id: 'Subject id',
    ems_party_name: 'Subject name',
    balancing_group_id: 'Balancing group id',
    balancing_group_name: 'Balancing group name',
    delivery_point_id: 'Delivery point id',
    delivery_point_name: 'Delivery point name',
    device_id: 'Device id',
    device_name: 'Device name',
    load_nomination: 'Nominated consumption [MWh]',
    load_plan: 'Planned consumption [MWh]',
    load_real: 'Real consumption [MWh]',
    load_imbalance: 'Consumption imbalance [MWh]',
    generation_nomination: 'Nominated generation [MWh]',
    generation_plan: 'Planned generation [MWh]',
    generation_real: 'Real generation [MWh]'
  },
  dateFrom: 'Date from',
  dateTo: 'Date to'
};

export default reporting;
